import { Printer } from "lucide-react";
import { Button } from "@/components/ui/button";
import { FlowerHeatmap } from "@/components/FlowerHeatmap";
import { ScoreBreakdown } from "@/components/ScoreBreakdown";
import { HeatLegend } from "@/components/HeatLegend";
import { CoachInvite } from "@/components/CoachInvite";
import { OverallInsight } from "@/components/OverallInsight";
import { useInsights } from "@/lib/useInsights";
import { AREAS, MAX_SCORE, total, fmtDate, fmtScore, band } from "@/lib/heat";
import { PrintMenu } from "@/components/PrintMenu";
import { TopicAnswers } from "@/components/TopicAnswers";

export const PrintButton = ({ testId = "print-report-button" }) => (
  <Button data-testid={testId} onClick={() => window.print()} className="no-print rounded-full bg-[#1F4266] hover:bg-[#122A43]">
    <Printer className="mr-2 h-4 w-4" /> Print / Save as PDF
  </Button>
);

const Meta = ({ label, value, testId }) => (
  <div>
    <div className="text-[10px] font-mono uppercase tracking-widest text-slate-500">{label}</div>
    <div className="mt-0.5 font-medium text-slate-900" data-testid={testId}>{value || "—"}</div>
  </div>
);

export const AreaInsights = ({ scores, insights, heading = "What each petal means" }) => (
  <section className="mt-8" data-testid="area-insights">
    <div className="mb-4 text-xs font-mono uppercase tracking-widest text-slate-500">{heading}</div>
    <div className="grid gap-4 md:grid-cols-2">
      {AREAS.map((a) => {
        const b = band(scores[a.key]);
        return (
          <div key={a.key} className="avoid-break rounded-xl border border-slate-200 p-4" data-testid={`area-insight-${a.key}`}>
            <div className="flex items-baseline gap-2">
              <span className="font-display font-semibold text-slate-900">{a.label}</span>
              <span className="text-xs font-semibold uppercase tracking-wider" style={{ color: b.hex }}>{b.label}</span>
              <span className="ml-auto font-mono text-xs text-slate-500">{fmtScore(scores[a.key])} / 36</span>
            </div>
            <p className="mt-2 text-sm leading-relaxed text-slate-600">{insights?.[a.key]?.[b.label] || ""}</p>
          </div>
        );
      })}
    </div>
  </section>
);

export const IndividualReportView = ({ data, max = MAX_SCORE, topics, descriptions }) => {
  const sum = total(data.scores);
  const ordered = [...AREAS].sort((a, b) => (data.scores[b.key] || 0) - (data.scores[a.key] || 0));
  const insights = useInsights();
  return (
    <article data-testid="individual-report" className="print-plain rounded-2xl border border-slate-200 bg-white p-6 sm:p-10 shadow-sm">
      <header className="flex flex-col-reverse sm:flex-row items-start justify-between gap-4 sm:gap-6 border-b border-slate-200 pb-6">
        <div className="flex items-center gap-4 min-w-0">
          <img src="/flower-logo.png" alt="" className="h-12 w-12 object-contain" />
          <div>
            <div className="text-xs font-mono uppercase tracking-widest text-slate-500">{data.entry_type === "group" ? "Group / Team Assessment Report" : "Personal Assessment Report"}</div>
            <h1 className="mt-1 text-3xl sm:text-4xl font-extrabold tracking-tight text-[#1F4266]" data-testid="report-person-name">
              {data.name}
            </h1>
          </div>
        </div>
        <PrintMenu />
      </header>
      <div className="mt-6 flex flex-wrap gap-x-10 gap-y-3 print:mt-4">
        <Meta label="Submitted" value={fmtDate(data.submitted_at)} testId="report-date" />
        {data.event_group && <Meta label="My Groups" value={data.event_group} testId="report-my-groups" />}
      </div>
      <section className="mt-8 grid gap-10 lg:grid-cols-[1.1fr_1fr] print:mt-4 print:grid-cols-2 print:gap-6 items-center">
        <div className="flex justify-center avoid-break fade-up print:[&_canvas]:!max-w-[280px]">
          <FlowerHeatmap scores={data.scores} max={max} size={440} />
        </div>
        <div className="space-y-6 fade-up-2">
          <div className="flex items-end gap-3">
            <span className="font-display text-5xl font-extrabold text-[#1F4266]" data-testid="report-total">{fmtScore(sum)}</span>
            <span className="pb-1.5 font-mono text-sm text-slate-500">/ {max * 5} total</span>
          </div>
          <ScoreBreakdown scores={data.scores} max={max} />
          <div className="grid grid-cols-2 gap-3 text-sm">
            <div className="rounded-xl bg-slate-50 p-3">
              <div className="text-[10px] font-mono uppercase tracking-widest text-slate-500">Strongest area</div>
              <div className="font-display font-semibold text-slate-900" data-testid="report-strongest">{ordered[0].label}</div>
            </div>
            <div className="rounded-xl bg-slate-50 p-3">
              <div className="text-[10px] font-mono uppercase tracking-widest text-slate-500">Growth area</div>
              <div className="font-display font-semibold text-slate-900" data-testid="report-growth">{ordered[4].label}</div>
            </div>
          </div>
        </div>
      </section>
      <div className="mt-6 space-y-4 print:mt-3 print:space-y-2">
        <HeatLegend />
        <CoachInvite />
      </div>
      <div className="print:break-before-page">
        <OverallInsight scores={data.scores} insights={insights} />
        <AreaInsights scores={data.scores} insights={insights} />
      </div>
      <TopicAnswers topics={topics} scores={data.scores} descriptions={descriptions} />
    </article>
  );
};
